/**
 * Proposal Generation Event Handlers
 *
 * This hook extends the AtomAssist event handlers with proposal-specific
 * functionality (dialogue ID syncing to ProposalGenerationStore).
 *
 * It reuses the core message handling logic from AtomAssist to maintain
 * consistency across chat products.
 */
import useAtomAssistEventHandlers from "src/views/assistant/hooks/useAtomAssistEventHandlers";
import { useCallback } from "react";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import { StreamingState } from "src/reusable/Chat/ChatStreaming/stores/StreamingStore";

const useProposalGenerationEventHandlers = () => {
  // Reuse AtomAssist event handlers for core functionality
  const { metadataHandler: atomAssistMetadataHandler, messageHandler } =
    useAtomAssistEventHandlers();

  // Get proposal-specific store actions
  const setDialogueId = useProposalGenerationStore(
    (state) => state.setDialogueId
  );

  // Extend metadata handler with proposal-specific logic
  const metadataHandler = useCallback(
    (metadata: any, streamingState: StreamingState) => {
      // Sync dialogue ID to proposal generation store if present
      if (metadata?.dialogue_id) {
        setDialogueId(metadata.dialogue_id);
      }

      // Delegate to AtomAssist handler for common functionality
      // (assigned_message_id, assistant_response_sources, etc.)
      atomAssistMetadataHandler(metadata, streamingState);
    },
    [setDialogueId, atomAssistMetadataHandler]
  );

  return {
    metadataHandler,
    messageHandler, // Reuse AtomAssist message handler directly
  };
};

export default useProposalGenerationEventHandlers;
